'use client';

import { cn } from '@/lib/utils';
import { customLink } from '@/utils';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import React, { HTMLAttributes } from 'react';
import { BiBell } from 'react-icons/bi';
import { MdOutlineStar, MdOutlineStarBorder } from 'react-icons/md';
import styles from './styles.module.css';

interface ICardProps extends HTMLAttributes<HTMLDivElement> {
  lang: string;
  cardImage: string | StaticImageData;
  cardAlt: string;
  href?: string;
  hoverCustomStyle?: string;
  customStyle?: any;
  customContentStyle?: string;
  hasSubtitle?: boolean;
  hoverContent?: JSX.Element;
  hasBell?: boolean;
  desktopVisible?: boolean;
  hasFavouriteButton?: boolean;
  onClickFavourite?: () => void;
  isFavourite?: boolean;
  sizes: string;
  imgClassName?: string;
}

export const Card: React.FC<ICardProps> = ({
  lang,
  cardImage,
  hoverCustomStyle,
  cardAlt,
  children,
  hoverContent,
  customStyle,
  hasSubtitle = false,
  customContentStyle,
  href = '/',
  hasBell = false,
  desktopVisible = false,
  hasFavouriteButton = false,
  isFavourite = false,
  onClickFavourite,
  sizes,
  imgClassName = '',
}) => {
  return (
    <Link
      tabIndex={-1}
      onDragStart={(e) => e.preventDefault()}
      className={cn(
        'group flex-auto aspect-[2/3] h-auto bg-zinc-300 overflow-hidden text-white relative cursor-pointer',
        customStyle,
        styles['card'],
        hoverCustomStyle ? `hover:[${hoverCustomStyle}]` : '',
      )}
      href={customLink(lang, href)}>
      <div className="h-full absolute left-0 top-0 w-full pointer-events-none overflow-hidden">
        <Image
          src={cardImage}
          alt=""
          fill
          quality={90}
          sizes={sizes}
          style={{ objectFit: 'cover' }}
          className={imgClassName}
        />
      </div>
      {hasFavouriteButton && (
        <button
          tabIndex={-1}
          aria-label="Favorite carousel item"
          className="absolute flex justify-between right-[10px] top-[10px] z-10"
          type="button"
          onClick={(event) => {
            event.preventDefault();
            onClickFavourite && onClickFavourite();
          }}>
          {isFavourite ? (
            <MdOutlineStar className="w-10 h-10" />
          ) : (
            <MdOutlineStarBorder className="w-10 h-10" />
          )}
        </button>
      )}
      {hasBell && (
        <div className="absolute flex justify-between items-center pl-[30px] right-[10px] top-[10px] w-[140px] bg-[rgba(0,0,0,.4)] rounded-[25px] uppercase">
          New
          <div className="flex justify-center items-center w-[50px] h-[50px] shadow-[0_4px_8px_0_rgba(0,0,0,0.2)] bg-primary rounded-full text-sm text-white text-[23px] z-30">
            <BiBell className="w-6 h-6" />
          </div>
        </div>
      )}

      {hasSubtitle ? (
        <div
          className={cn(
            styles['view-subtitle'] ? styles['view-subtitle'] : '',
          )}>
          {children}
          {hoverContent && (
            <div
              className={cn(
                'invisible',
                styles['subtitle-animated'] ? styles['subtitle-animated'] : '',
              )}>
              {hoverContent}
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            customContentStyle
              ? customContentStyle
              : 'absolute bottom-0 p-5 z-10 w-full',
          )}>
          {children}
          {hoverContent && (
            <div
              className={cn(
                desktopVisible
                  ? 'visible desktop:invisible !opacity-100'
                  : 'invisible',
                styles['view'] ? styles['view'] : '',
              )}>
              {hoverContent}
            </div>
          )}
        </div>
      )}
    </Link>
  );
};
